/**
 * Connections management (plan §12.3, §12.5).
 *
 * Create / rotate / revoke, with the show-once token modal. The capability profile picker is
 * driven by the server's own allowlist (`profiles` on the list response), so the UI can never
 * offer an access level the API will refuse — and adding a profile server-side needs no UI change.
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client.js";
import { Banner, Empty, ErrorNote, Loading, Modal, TokenModal, formatDate } from "../components/ui.js";
import { useSession } from "../state/session.js";
import type { CapabilityProfile, ClientType, ConnectionsResponse, IssuedConnection } from "../api/types.js";

/** Customer vocabulary for the enums. No provider jargon anywhere (§23.3). */
const CLIENT_LABEL: Record<ClientType, string> = {
  owner: "API credential",
  hark: "Hark",
  browser_extension: "Browser capture",
  agent: "AI agent",
  custom: "Other",
};

const PROFILE_LABEL: Record<CapabilityProfile, string> = {
  "owner-full": "Full access",
  "hark-standard": "Hark companion",
  "agent-recall": "Read and recall",
  "agent-capture": "Read, recall, and capture",
  "browser-capture": "Capture pages",
  "read-only": "Read only",
  "custom-advanced": "Custom",
};

const CREATABLE: ClientType[] = ["agent", "owner", "browser_extension", "custom"];

const DEFAULT_ALLOWED: Record<ClientType, CapabilityProfile[]> = {
  owner: ["owner-full", "read-only"],
  hark: ["hark-standard"],
  browser_extension: ["browser-capture", "read-only"],
  agent: ["agent-recall", "agent-capture", "read-only"],
  custom: ["read-only"],
};

export default function Connections() {
  const { me } = useSession();
  const [data, setData] = useState<ConnectionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [issued, setIssued] = useState<IssuedConnection | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<{ id: string; name: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [displayName, setDisplayName] = useState("");
  const [clientType, setClientType] = useState<ClientType>("agent");
  const [profile, setProfile] = useState<CapabilityProfile>("agent-recall");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.connections());
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const allowed = useMemo(
    () => (data?.profiles ?? DEFAULT_ALLOWED)[clientType] ?? DEFAULT_ALLOWED[clientType],
    [data, clientType],
  );

  useEffect(() => {
    if (!allowed.includes(profile)) setProfile(allowed[0] ?? "read-only");
  }, [allowed, profile]);

  if (!me) return null;
  if (loading) return <Loading label="Loading your connections" />;

  const ready = me.deployment?.status === "READY";
  const connections = data?.connections ?? [];
  const active = connections.filter((c) => c.status !== "revoked");
  const revoked = connections.filter((c) => c.status === "revoked");

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const result = await api.createConnection({
        displayName: displayName.trim(),
        clientType,
        capabilityProfile: profile,
      });
      setIssued(result);
      setDisplayName("");
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setCreating(false);
    }
  }

  async function onRotate(id: string) {
    setBusyId(id);
    setError(null);
    try {
      setIssued(await api.rotateConnection(id));
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusyId(null);
    }
  }

  async function onRevoke(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await api.revokeConnection(id);
      setConfirmRevoke(null);
      await load();
    } catch (err) {
      setError(err);
      setConfirmRevoke(null);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <div className="page-head">
        <p className="eyebrow">Connections</p>
        <h1>Apps and agents</h1>
        <p className="lede">
          Every app that reaches your Construct holds its own key with its own access level.
          Revoking one never affects the others. If an agent gave you a short code to type,{" "}
          <Link to="/link">enter it here</Link>.
        </p>
      </div>

      {error ? <ErrorNote error={error} onRetry={() => void load()} /> : null}

      {!ready ? (
        <Banner kind="warn" title="Your Construct is not ready">
          You can create connections once it is up. <Link to="/dashboard">Check its status</Link>
        </Banner>
      ) : null}

      {/* ---- create --------------------------------------------------------- */}
      <div className="card">
        <h2>New connection</h2>
        <form onSubmit={onCreate}>
          <label className="field">
            <span className="field__label">What is it for?</span>
            <input
              className="input"
              type="text"
              required
              maxLength={80}
              placeholder="Claude Code (laptop)"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <span className="field__hint">A name you will recognise later. Only you see it.</span>
          </label>

          <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
            <label className="field">
              <span className="field__label">Kind</span>
              <select
                className="select"
                value={clientType}
                onChange={(e) => setClientType(e.target.value as ClientType)}
              >
                {CREATABLE.map((t) => (
                  <option key={t} value={t}>
                    {CLIENT_LABEL[t]}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span className="field__label">Access level</span>
              <select
                className="select"
                value={profile}
                onChange={(e) => setProfile(e.target.value as CapabilityProfile)}
              >
                {allowed.map((p) => (
                  <option key={p} value={p}>
                    {PROFILE_LABEL[p] ?? p}
                  </option>
                ))}
              </select>
              <span className="field__hint">Give each app the least it needs.</span>
            </label>
          </div>

          <div className="btn-row">
            <button type="submit" className="btn btn--primary" disabled={!ready || creating || !displayName.trim()}>
              {creating ? "Creating" : "Create connection"}
            </button>
          </div>
        </form>
      </div>

      {/* ---- list ----------------------------------------------------------- */}
      <h2 style={{ marginTop: 40, marginBottom: 16 }}>Active</h2>
      {active.length === 0 ? (
        <Empty title="No connections yet">
          <p>When you connect an app or an agent, it will show up here.</p>
        </Empty>
      ) : (
        <div className="card table-scroll" style={{ padding: 0 }}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Kind</th>
                <th scope="col">Access</th>
                <th scope="col">Created</th>
                <th scope="col">Last used</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {active.map((c) => (
                <tr key={c.id}>
                  <td>
                    {c.displayName}
                    {/*
                      An OAuth-linked agent is an ordinary connection with an ordinary principal;
                      the badge only records HOW it got here, which is the one thing the customer
                      cannot otherwise tell. Rotate and revoke are unchanged (§2.2).
                    */}
                    {c.linkedVia === "oauth" ? (
                      <span className="pill pill--idle" style={{ marginLeft: 10 }}>
                        Linked via OAuth
                      </span>
                    ) : null}
                    {c.linkedVia === "oauth" && c.clientLabel && c.clientLabel !== c.displayName ? (
                      <span className="muted small" style={{ marginLeft: 10 }}>
                        {c.clientLabel}
                      </span>
                    ) : null}
                  </td>
                  <td className="muted">{CLIENT_LABEL[c.clientType]}</td>
                  <td className="muted">{PROFILE_LABEL[c.capabilityProfile] ?? c.capabilityProfile}</td>
                  <td className="muted mono small">{formatDate(c.issuedAt)}</td>
                  <td className="muted mono small">{c.lastUsedAt ? formatDate(c.lastUsedAt) : "never"}</td>
                  <td>
                    <div className="row">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={busyId !== null}
                        onClick={() => void onRotate(c.id)}
                      >
                        {busyId === c.id ? "Working" : "Rotate"}
                      </button>
                      <button
                        type="button"
                        className="btn btn--danger btn--sm"
                        disabled={busyId !== null}
                        onClick={() => setConfirmRevoke({ id: c.id, name: c.displayName })}
                      >
                        Revoke
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {revoked.length > 0 ? (
        <>
          <h2 style={{ marginTop: 40, marginBottom: 16 }}>Revoked</h2>
          <div className="card table-scroll" style={{ padding: 0 }}>
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Kind</th>
                  <th scope="col">Revoked</th>
                </tr>
              </thead>
              <tbody>
                {revoked.map((c) => (
                  <tr key={c.id}>
                    <td className="muted">{c.displayName}</td>
                    <td className="muted">{CLIENT_LABEL[c.clientType]}</td>
                    <td className="muted mono small">{formatDate(c.revokedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      {issued ? (
        <TokenModal
          title={issued.connection.displayName}
          token={issued.token}
          mcpConfig={issued.config}
          endpointUrl={issued.endpoint.mcpUrl}
          onClose={() => setIssued(null)}
        />
      ) : null}

      {confirmRevoke ? (
        <Modal title="Revoke connection" onClose={() => setConfirmRevoke(null)}>
          <p className="modal__id">Confirm</p>
          <h2>Revoke {confirmRevoke.name}?</h2>
          <p className="muted">
            That app will stop being able to reach your Construct immediately. Nothing is deleted,
            and no other connection is affected.
          </p>
          <div className="btn-row">
            <button
              type="button"
              className="btn btn--danger"
              disabled={busyId !== null}
              onClick={() => void onRevoke(confirmRevoke.id)}
            >
              {busyId ? "Revoking" : "Revoke it"}
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setConfirmRevoke(null)}>
              Keep it
            </button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
